import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Label,
  useCreateLabel,
  useDeleteLabel,
  useMoveBoardLabel,
  useUpdateLabel,
} from '@/entities/board';
import { FormError } from '@/shared/forms';
import { dropMove } from '@/shared/lib/reorder';
import { Button, Dialog } from '@/shared/ui';

import { isBoardLabel } from './dnd';
import { LabelForm } from './LabelForm';
import { LabelRow } from './LabelRow';
import styles from './LabelManager.module.css';

const NEW = '__new__';

interface Props {
  boardId: string;
  labels: Label[];
  onClose: () => void;
}

/**
 * Board-level label management: create, rename, recolor, delete. Labels belong
 * to the board and exist independently of cards; a label is removed only by an
 * explicit delete here (which also takes it off all cards).
 */
export function LabelManager({ boardId, labels, onClose }: Props) {
  const { t } = useTranslation();
  // Label id being edited, or NEW for the create form.
  const [editing, setEditing] = useState<string | null>(null);
  const createLabel = useCreateLabel(boardId);
  const updateLabel = useUpdateLabel(boardId);
  const deleteLabel = useDeleteLabel(boardId);
  const moveLabel = useMoveBoardLabel(boardId);
  const error = createLabel.error ?? updateLabel.error ?? deleteLabel.error ?? moveLabel.error;

  // Board-level order (also the filter bar's order), changed by drag and drop only.
  const ids = labels.map((l) => l.id);
  const onDrop = useEffectEvent((labelId: string, targetId: string, edge: Edge | null) => {
    const result = dropMove(ids, labelId, targetId, edge === 'top' ? 'before' : 'after', 'label');
    if (!result) return;
    moveLabel.mutate({ labelId, anchor: result.anchor, order: result.order });
  });
  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => isBoardLabel(source.data) && source.data.boardId === boardId,
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0];
          if (!target || !isBoardLabel(source.data) || !isBoardLabel(target.data)) return;
          onDrop(source.data.labelId, target.data.labelId, extractClosestEdge(target.data));
        },
      }),
    [boardId],
  );
  const done = () => {
    setEditing(null);
  };

  return (
    <Dialog title={t('labels.manageTitle')} onClose={onClose}>
      <p className={styles.hint}>{t('labels.manageHint')}</p>
      <FormError error={error} />
      {labels.length === 0 && editing !== NEW && (
        <p className={styles.empty}>{t('labels.boardEmpty')}</p>
      )}
      <ul className={styles.list}>
        {labels.map((label) =>
          editing === label.id ? (
            <li key={label.id}>
              <LabelForm
                initial={label}
                submitLabel={t('common.save')}
                pending={updateLabel.isPending || deleteLabel.isPending}
                onCancel={done}
                onSubmit={(value) => {
                  updateLabel.mutate({ labelId: label.id, ...value }, { onSuccess: done });
                }}
                onDelete={() => {
                  deleteLabel.mutate(label.id, { onSuccess: done });
                }}
              />
            </li>
          ) : (
            <LabelRow
              key={label.id}
              boardId={boardId}
              label={label}
              reorderable={labels.length > 1}
              onEdit={() => {
                setEditing(label.id);
              }}
            />
          ),
        )}
      </ul>
      {editing === NEW ? (
        <LabelForm
          submitLabel={t('labels.create')}
          pending={createLabel.isPending}
          onCancel={done}
          onSubmit={(value) => {
            createLabel.mutate(value, { onSuccess: done });
          }}
        />
      ) : (
        <div>
          <Button
            variant="primary"
            onClick={() => {
              setEditing(NEW);
            }}
          >
            + {t('labels.create')}
          </Button>
        </div>
      )}
    </Dialog>
  );
}
